const NESTED_FIELD_TOKEN = /\s+[-]?[A-Za-z0-9_.]+:/;
export const INVALID_GLOBAL_SEARCH_QUERY = '_id:"__INVALID_GLOBAL_SEARCH__"';

// grid-common's transformLuceneValues({ isWildcard: true }) backslash-escapes these
// characters before the search text reaches this app; undo it to get the typed value.
const GRID_COMMON_ESCAPED_CHAR = /\\([+\-&|!(){}[\]^"~;:\\])/g;
// In a wildcard query only *, ? and \ are special; hyphen, space and _ are literal.
const WILDCARD_SPECIAL_CHAR = /[\\*?]/g;
// Every query_string control char, including space and hyphen, so input stays one term.
const QUERY_STRING_SPECIAL_CHAR = /[+\-=&|><!(){}[\]^"~*?:\\/ ]/g;

const hasFieldValueBoundary = (query, cursor) => {
  let index = cursor;
  if (query[index] !== " ") {
    return false;
  }

  while (query[index] === " ") {
    index++;
  }

  const nextThreeChars = query.slice(index, index + 3).toUpperCase();
  if (nextThreeChars === "AND" && query[index + 3] === " ") {
    return true;
  }

  const nextTwoChars = query.slice(index, index + 2).toUpperCase();
  return nextTwoChars === "OR" && query[index + 2] === " ";
};

const expandWildcardFieldValue = (fieldToken, fieldValue) => {
  if (typeof fieldToken !== "string" || !fieldToken || typeof fieldValue !== "string") {
    return null;
  }

  const normalizedValue = fieldValue.trim();
  if (!normalizedValue.startsWith("*") || !normalizedValue.endsWith("*")) {
    return null;
  }

  if (normalizedValue.includes("\\ ")) {
    return null;
  }

  const innerValue = normalizedValue.slice(1, -1).trim();
  if (!innerValue || !innerValue.includes(" ")) {
    return null;
  }

  const terms = innerValue.split(/\s+/).filter(Boolean);
  if (terms.length < 2) {
    return null;
  }

  return `(${terms.map((term) => `${fieldToken}:*${escapeUnescapedSpaces(term)}*`).join(" AND ")})`;
};

export const escapeUnescapedSpaces = (value) => {
  if (typeof value !== "string" || !value) {
    return value;
  }

  let escapedValue = "";
  for (let index = 0; index < value.length; index++) {
    const char = value[index];
    if (char === " " && value[index - 1] !== "\\") {
      escapedValue += "\\ ";
    } else {
      escapedValue += char;
    }
  }

  return escapedValue;
};

// Accepts the field list as an array or as the legacy "'f1','f2'" string.
const toGlobalSearchFields = (fields) => {
  const list = Array.isArray(fields)
    ? fields
    : typeof fields === "string"
      ? fields.split(",").map((field) => field.trim().replace(/^['"]|['"]$/g, ""))
      : [];
  const baseFields = list
    .filter((field) => typeof field === "string" && field.trim())
    .map((field) => field.trim().replace(/\.keyword$/, ""));
  return [...new Set(baseFields)];
};

export const normalizeGlobalSearchText = (searchText) => {
  if (typeof searchText !== "string") {
    return "";
  }

  const text = searchText.replace(GRID_COMMON_ESCAPED_CHAR, "$1").trim();
  // Punctuation-only input (e.g. "....") must never become a match-all wildcard.
  return /[A-Za-z0-9]/.test(text) ? text : "";
};

/**
 * Global search = case-insensitive "contains" of the whole input within ONE column.
 * The input is never split into words: hyphen, space and underscore are literal.
 *
 * - wildcard on <field>.keyword: whole stored value, so "AB2026-0828" or "0 man" can match.
 * - wildcard on <field>: mirrors in-column contains matching for non-keyword mappings.
 *
 * We intentionally do not add a multi_match phrase fallback here, because phrase clauses can
 * broaden matches compared with in-column contains filters and produce unexpected extra rows.
 *
 * Returns null when there is nothing searchable.
 */
export const buildGlobalSearchQuery = (searchText, fields) => {
  const text = normalizeGlobalSearchText(searchText);
  if (!text) {
    return null;
  }

  const baseFields = toGlobalSearchFields(fields);
  if (!baseFields.length) {
    return {
      query_string: {
        query: `*${text.replace(QUERY_STRING_SPECIAL_CHAR, "\\$&")}*`,
        analyze_wildcard: true
      }
    };
  }

  const value = `*${text.replace(WILDCARD_SPECIAL_CHAR, "\\$&")}*`;
  return {
    bool: {
      should: [
        ...baseFields.map((field) => ({
          wildcard: { [`${field}.keyword`]: { value, case_insensitive: true } }
        })),
        ...baseFields.map((field) => ({
          wildcard: { [field]: { value, case_insensitive: true } }
        }))
      ],
      minimum_should_match: 1
    }
  };
};

export const exactSearchString = (values, shouldEscapeSpaces = true) => {
  if (!Array.isArray(values)) {
    throw new TypeError("Expected values to be an array");
  }

  return values.map((value) => {
    const normalizedValue = typeof value === "string" ? value.trim() : value;
    const escapedValue =
      typeof normalizedValue === "string" && shouldEscapeSpaces
        ? escapeUnescapedSpaces(normalizedValue)
        : normalizedValue;
    return `"${escapedValue}"`;
  });
};

export const buildContainsQuery = (field, value) => {
  if (typeof value !== "string") {
    return `${field}:*${value}*`;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return `${field}:**`;
  }

  const parts = normalizedValue.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return `${field}:*${escapeUnescapedSpaces(parts[0])}*`;
  }

  const andQuery = parts.map((part) => `${field}:*${escapeUnescapedSpaces(part)}*`).join(" AND ");
  return `(${andQuery})`;
};

export const escapeLuceneFieldValueSpaces = (query) => {
  if (typeof query !== "string" || !query) {
    return query;
  }

  const protectedRanges = [];
  const protectRanges = (value) =>
    value.replace(/[^\s()]+:\s*(?:\[[^[\]{}]*\sTO\s[^[\]{}]*\]|\{[^[\]{}]*\sTO\s[^[\]{}]*\})/gi, (match) => {
      const token = `__LUCENE_RANGE_${protectedRanges.length}__`;
      protectedRanges.push(match);
      return token;
    });

  const restoreRanges = (value) =>
    protectedRanges.reduce(
      (result, rangeQuery, index) => result.replace(`__LUCENE_RANGE_${index}__`, rangeQuery),
      value
    );

  const safeQuery = protectRanges(query);

  let escapedQuery = "";

  for (let index = 0; index < safeQuery.length; index++) {
    const char = safeQuery[index];
    if (char !== ":") {
      escapedQuery += char;
      continue;
    }

    const nextChar = safeQuery[index + 1];
    if (!nextChar || ["\"", "[", "{", "("].includes(nextChar)) {
      escapedQuery += char;
      continue;
    }

    // Capture the field token immediately before ':', if present.
    const matchedTokens = safeQuery.slice(0, index).match(/([^\s()]+)$/);
    const fieldToken = matchedTokens ? matchedTokens[1] : "";

    let cursor = index + 1;
    while (cursor < safeQuery.length) {
      if (safeQuery[cursor] === ")") {
        break;
      }
      if (hasFieldValueBoundary(safeQuery, cursor)) {
        break;
      }
      cursor++;
    }

    const fieldValue = safeQuery.slice(index + 1, cursor);
    const trimmedFieldValue = fieldValue.trim();
    const expandedValue = expandWildcardFieldValue(fieldToken, fieldValue);
    if (expandedValue) {
      if (fieldToken && escapedQuery.endsWith(fieldToken)) {
        escapedQuery = escapedQuery.slice(0, -fieldToken.length);
      }
      escapedQuery += expandedValue;
      index = cursor - 1;
      continue;
    }

    // Keep range expressions untouched even if there are spaces after ':',
    // e.g. "field: [start TO end]".
    if (
      ((trimmedFieldValue.startsWith("[") && trimmedFieldValue.endsWith("]"))
        || (trimmedFieldValue.startsWith("{") && trimmedFieldValue.endsWith("}")))
      && /\sTO\s/i.test(trimmedFieldValue)
    ) {
      escapedQuery += `${char}${fieldValue}`;
      index = cursor - 1;
      continue;
    }

    // Keep legacy composite clauses untouched, e.g. "status:'Active' status:'Pending'".
    // Escaping that internal space turns a tolerated expression into invalid Lucene.
    if (NESTED_FIELD_TOKEN.test(fieldValue)) {
      escapedQuery += `${char}${fieldValue}`;
      index = cursor - 1;
      continue;
    }

    escapedQuery += `${char}${escapeUnescapedSpaces(fieldValue)}`;
    index = cursor - 1;
  }

  return restoreRanges(escapedQuery);
};